import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiClient } from '../../services/api';
import { toast } from 'react-toastify';
import { Campaign } from '../../types';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';

interface DailyNotesPanelProps {
  notes: any[];
  date: string;
  campaignId?: string;
  campaigns: Campaign[];
  canManage: boolean;
  onRefresh: () => void;
  density?: DashboardDensity;
  embedded?: boolean;
}

const DailyNotesPanel: React.FC<DailyNotesPanelProps> = ({
  notes,
  date,
  campaignId,
  campaigns,
  canManage,
  onRefresh,
  density = 'standard',
  embedded = false,
}) => {
  const [note, setNote] = useState('');
  const [noteCampaignId, setNoteCampaignId] = useState(campaignId || '');
  const [saving, setSaving] = useState(false);
  const compact = isCompact(density);

  const save = async () => {
    if (!note.trim()) return;
    try {
      setSaving(true);
      await apiClient.operations.createDailyNote({
        workDate: date,
        campaignId: noteCampaignId || undefined,
        note,
      });
      setNote('');
      toast.success('Note added');
      onRefresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.operations.deleteDailyNote(id);
      onRefresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete note');
    }
  };

  const content = (
    <>
      {!embedded && (
        <Typography variant="h6" fontWeight={700} gutterBottom>Operations Log</Typography>
      )}
      {canManage && (
        <Stack spacing={compact ? 0.5 : 1} sx={{ mb: compact ? 1 : 1.5 }}>
          <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: 32, fontSize: '0.75rem' } }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Campaign</InputLabel>
            <Select value={noteCampaignId} label="Campaign" onChange={(e) => setNoteCampaignId(e.target.value)}>
              <MenuItem value="">General</MenuItem>
              {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={compact ? 1 : 2}
            placeholder="Add operations note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
          />
          <Button size="small" variant="contained" onClick={save} disabled={saving || !note.trim()} sx={{ fontSize: '0.7rem' }}>
            Add
          </Button>
        </Stack>
      )}
      <Stack spacing={compact ? 0.5 : 0.75}>
        {notes.length === 0 && (
          <Typography variant="caption" color="text.secondary">No notes yet.</Typography>
        )}
        {notes.map((n) => (
          <Box key={n.id} sx={{ p: compact ? 0.75 : 1, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.72rem', lineHeight: 1.3 }}>{n.note}</Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                {n.createdByName} · {new Date(n.createdAt).toLocaleTimeString()}
              </Typography>
              {canManage && (
                <IconButton size="small" color="error" onClick={() => remove(n.id)} sx={{ p: 0.25 }}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </>
  );

  if (embedded) return <Box>{content}</Box>;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent sx={{ p: compact ? 1.5 : 2 }}>{content}</CardContent>
    </Card>
  );
};

export default DailyNotesPanel;
